import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getBreakdown, getFunnel, getPrograms, getRuns, getTrends } from "../api/client.js";
import { Shell } from "../components/Shell.jsx";
import { HBarChart, Sparkline, StageFunnel, VBarChart } from "../components/charts.jsx";
import { Icon } from "../components/Icon.jsx";
import {
  EvidencePeriodHeader,
  Loading,
  ErrorCard,
  MetricValue,
  Panel,
  formatCount,
} from "../components/ui.jsx";
import { ENROLLMENT_DETECTION, getActionDetection } from "../opportunityDetection.js";
import { DOMAIN_LABELS, actionStages, sortActionsByStage } from "../domainModel.js";
import { campaignBuilderRoute, opportunitiesRoute } from "../navConfig.js";

function describeAudienceChange(current, previous, previousPeriod, minimum) {
  if (current?.suppressed) {
    return `Comparison is withheld because the current audience is below the privacy threshold of ${minimum}.`;
  }
  if (!previousPeriod) return "No prior evidence period is available for comparison.";
  if (!previous) return `New recommendation since evidence period ${previousPeriod}.`;
  if (previous.suppressed) {
    return `Comparison with ${previousPeriod} is unavailable because the prior audience was suppressed.`;
  }
  if (current?.memberCount == null || previous.memberCount == null) {
    return `Audience comparison with ${previousPeriod} is unavailable.`;
  }

  const delta = current.memberCount - previous.memberCount;
  if (delta === 0) return `No change from evidence period ${previousPeriod}.`;
  return `${delta > 0 ? "Increased" : "Decreased"} by ${formatCount(Math.abs(delta))} covered people since ${previousPeriod}.`;
}

function RecommendationCard({
  title,
  detectionInfo,
  audience,
  suppressed,
  minimum,
  change,
  programName,
  evidencePeriod,
  stages,
  actionHref,
  blockedReason,
  featured = false,
}) {
  return (
    <article className={`recommended-action-card${featured ? " goal-card" : ""}`}>
      <header className="recommendation-header">
        <div>
          <h3>{title}</h3>
          <div className="recommendation-context" aria-label="Recommendation context">
            <span>{programName}</span>
            <span>Evidence period {evidencePeriod || "Unavailable"}</span>
          </div>
        </div>
        <div className="recommendation-audience">
          <span className="recommendation-label">Aggregate audience</span>
          <MetricValue value={audience} grain="covered people" suppressed={suppressed} minimum={minimum} />
        </div>
      </header>

      <div className="recommendation-decision-grid">
        <div>
          <span className="recommendation-label">What changed</span>
          <p>{change}</p>
        </div>
        <div>
          <span className="recommendation-label">Why it matters</span>
          <p>{detectionInfo.opportunity}</p>
        </div>
      </div>

      <div className="action-stage-list" aria-label="Included clinical stages">
        {stages.length > 0 ? (
          stages.map((stage) => (
            <span key={stage.id} className={`stage-button tier-${stage.tier}`}>
              {stage.label || `Stage ${stage.id}: ${stage.name}`}
            </span>
          ))
        ) : (
          <span className="stage-button tier-low">Recommendation-level cohort</span>
        )}
      </div>

      <details className="recommendation-detection">
        <summary>Detection details</summary>
        <div>
          <p>
            <strong>How detected:</strong> {detectionInfo.detection}
          </p>
          <p>
            <strong>Important limitation:</strong> {detectionInfo.caution}
          </p>
        </div>
      </details>

      <div className="recommendation-next-action">
        <span className="recommendation-label">Next best action</span>
        {blockedReason ? (
          <>
            <span className="recommended-action-link bi-btn-primary disabled" aria-disabled="true">
              Create campaign
            </span>
            <p className="suppression-action-note" role="note">
              {blockedReason}
            </p>
          </>
        ) : (
          <Link className="recommended-action-link bi-btn-primary" to={actionHref}>
            Create campaign
          </Link>
        )}
      </div>
    </article>
  );
}

export function OpportunitiesExplorer({ tenant, enums, params, auth }) {
  const [domain, setDomain] = useState(params.get("programDomain") || "msk");
  const [runMonth, setRunMonth] = useState("");
  const [runs, setRuns] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [bands, setBands] = useState(null);
  const [subdomains, setSubdomains] = useState(null);
  const [suppressions, setSuppressions] = useState(null);
  const [actions, setActions] = useState(null);
  const [previousActions, setPreviousActions] = useState(null);
  const [previousRunMonth, setPreviousRunMonth] = useState(null);
  const [trends, setTrends] = useState(null);
  const [programs, setPrograms] = useState(null);
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);
  const [error, setError] = useState(null);
  const moreInfoTriggerRef = useRef(null);
  const moreInfoDialogRef = useRef(null);

  useEffect(() => {
    getPrograms()
      .then((data) => setPrograms(data.programs))
      .catch(setError);
  }, []);

  useEffect(() => {
    setError(null);
    setRuns(null);
    setFunnel(null);
    getRuns(domain)
      .then((data) => setRuns(data.runs))
      .catch(setError);
    getTrends(domain).then(setTrends).catch(setError);
  }, [domain]);

  useEffect(() => {
    if (!runs?.length) return;
    const latestMonth = [...runs].sort((a, b) => b.runMonth.localeCompare(a.runMonth))[0].runMonth;
    if (runMonth !== latestMonth) setRunMonth(latestMonth);
  }, [runs, runMonth]);

  useEffect(() => {
    if (!runs?.length) return;
    setFunnel(null);
    setError(null);
    const orderedRuns = [...runs].sort((a, b) => b.runMonth.localeCompare(a.runMonth));
    const month = orderedRuns[0].runMonth;
    if (month !== runMonth) {
      setRunMonth(month);
      return;
    }
    const previousRun = orderedRuns[1] || null;
    setPreviousActions(null);
    setPreviousRunMonth(previousRun?.runMonth || null);
    Promise.all([
      getFunnel(domain, month),
      getBreakdown(domain, "band", month),
      getBreakdown(domain, "subdomain", month),
      getBreakdown(domain, "suppression_reason", month),
      getBreakdown(domain, "recommended_action", month),
      previousRun
        ? getBreakdown(domain, "recommended_action", previousRun.runMonth)
        : Promise.resolve({ rows: [] }),
    ])
      .then(([funnelData, bandData, subData, supData, actionData, previousActionData]) => {
        setFunnel(funnelData);
        setBands(bandData.rows);
        setSubdomains(subData.rows);
        setSuppressions(supData.rows);
        setActions(actionData.rows);
        setPreviousActions(previousActionData.rows);
      })
      .catch(setError);
  }, [domain, runMonth, runs]);

  useEffect(() => {
    if (!domain) return;
    window.history.replaceState(null, "", opportunitiesRoute({ programDomain: domain }));
  }, [domain]);

  useEffect(() => {
    if (!moreInfoOpen) return undefined;

    const dialog = moreInfoDialogRef.current;
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = () =>
      [...(dialog?.querySelectorAll(focusableSelector) || [])].filter((element) => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        const closedDisclosure = element.closest("details:not([open])");
        const isDisclosureSummary = closedDisclosure?.querySelector(":scope > summary") === element;
        return (
          (!closedDisclosure || isDisclosureSummary) &&
          element.getClientRects().length > 0 &&
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== "none" &&
          style.visibility !== "hidden"
        );
      });
    focusable()[0]?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMoreInfoOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const trigger = moreInfoTriggerRef.current;
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      trigger?.focus();
    };
  }, [moreInfoOpen]);

  const activeRun = runs?.find((run) => run.runMonth === runMonth);
  const gateFailed = activeRun?.qualityGate?.status === "fail";
  const sortedActions = sortActionsByStage(enums, domain, actions || []);
  const activeProgram = programs?.find((p) => p.programDomain === domain);
  const activeProgramName = activeProgram?.name || DOMAIN_LABELS[domain];
  const goalOneCount =
    activeProgram?.opportunitiesOnNotEnrolledWithDependentsCount ??
    activeProgram?.opportunitiesOnNotEnrolledCount ??
    activeRun?.opportunityCount ??
    0;
  const goalOneSuppressed = goalOneCount > 0 && goalOneCount < enums.minCohortSize;
  const previousActionsByLabel = new Map((previousActions || []).map((action) => [action.label, action]));
  const goalOneHash = campaignBuilderRoute({
    source: "recommendation",
    programDomain: domain,
    runMonth: activeRun?.runMonth,
    activationGoal: "goal1_enrollment",
  });
  const body = () => {
    if (error) return <ErrorCard error={error} />;
    if (!runs || !funnel || !programs) return <Loading />;

    return (
      <>
        <div className="opportunity-toolbar">
          <div className="opportunity-tabs" role="group" aria-label="Programs">
            {enums.programDomains.map((d) => (
              <button
                key={d}
                className={"chip" + (d === domain ? " on" : "")}
                onClick={() => {
                  setDomain(d);
                  setRunMonth("");
                }}
                aria-pressed={d === domain}
              >
                {DOMAIN_LABELS[d]}
              </button>
            ))}
          </div>
          <button
            ref={moreInfoTriggerRef}
            className="bi-btn-ghost compact"
            type="button"
            onClick={() => setMoreInfoOpen(true)}
          >
            <Icon name="diagram" size={15} /> More info
          </button>
        </div>

        <EvidencePeriodHeader
          period={activeRun?.runMonth}
          refreshedAt={activeRun?.completedAt || activeRun?.asOfDate}
          freshness="current"
          freshnessLabel="Latest available"
        />

        <Panel
          title="Recommended actions"
          desc="Enrollment and usage actions matched to the selected program"
          full
        >
          <div className="recommended-action-grid">
            <RecommendationCard
              title="Enroll people with a current-condition benefit fit"
              detectionInfo={ENROLLMENT_DETECTION.goal1}
              audience={goalOneCount}
              suppressed={goalOneSuppressed}
              minimum={enums.minCohortSize}
              change="Prior-period audience history is unavailable for this enrollment recommendation."
              programName={activeProgramName}
              evidencePeriod={activeRun?.runMonth}
              stages={[{ id: "enrollment", label: "Enrollment cohort: not enrolled", tier: "medium" }]}
              actionHref={goalOneHash}
              blockedReason={
                goalOneSuppressed
                  ? `Campaign creation is unavailable because this audience is below the approved privacy threshold of ${enums.minCohortSize} covered people.`
                  : gateFailed
                    ? "Campaign creation is unavailable because this evidence period did not pass quality checks."
                    : goalOneCount <= 0
                      ? "Campaign creation is unavailable because no eligible audience was measured for this recommendation."
                      : null
              }
              featured
            />
            {sortedActions.map((row) => {
              const stages = actionStages(enums, domain, row.label);
              const detectionInfo = getActionDetection(domain, row.label);
              const blockedReason = row.suppressed
                ? `Campaign creation is unavailable because this audience is below the approved privacy threshold of ${enums.minCohortSize} covered people.`
                : gateFailed
                  ? "Campaign creation is unavailable because this evidence period did not pass quality checks."
                  : row.memberCount <= 0
                    ? "Campaign creation is unavailable because no eligible audience was measured for this recommendation."
                    : null;
              const actionHash = campaignBuilderRoute({
                source: "recommendation",
                programDomain: domain,
                runMonth: activeRun?.runMonth,
                recommendedAction: row.label,
                activationGoal: "goal2_usage",
              });
              return (
                <RecommendationCard
                  key={row.key}
                  title={row.label}
                  detectionInfo={detectionInfo}
                  audience={row.memberCount}
                  suppressed={row.suppressed}
                  minimum={enums.minCohortSize}
                  change={describeAudienceChange(
                    row,
                    previousActionsByLabel.get(row.label),
                    previousRunMonth,
                    enums.minCohortSize
                  )}
                  programName={activeProgramName}
                  evidencePeriod={activeRun?.runMonth}
                  stages={stages}
                  actionHref={actionHash}
                  blockedReason={blockedReason}
                />
              );
            })}
          </div>
        </Panel>

        <div className="opportunity-main-grid">
          <Panel
            title={`${DOMAIN_LABELS[domain]} stage funnel`}
            desc="Distinct covered people per clinical stage (guardrail-flagged people excluded)"
            full
          >
            <StageFunnel
              title={`${DOMAIN_LABELS[domain]} covered people by clinical stage`}
              stages={funnel.stages}
              minCohortSize={enums.minCohortSize}
            />
            <div style={{ marginTop: 10, fontSize: 12, color: "var(--bi-muted)" }}>
              {formatCount(funnel.guardrailCount)} covered people are suppressed by guardrails and excluded
              from campaign audiences.
            </div>
          </Panel>
        </div>

        {moreInfoOpen && (
          <div className="modal-backdrop" role="presentation" onMouseDown={() => setMoreInfoOpen(false)}>
            <div
              ref={moreInfoDialogRef}
              className="modal-panel opportunity-more-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="more-info-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="modal-head">
                <div>
                  <h2 id="more-info-title">{DOMAIN_LABELS[domain]} more info</h2>
                  <p>Secondary charts for evidence period {activeRun?.runMonth}</p>
                </div>
                <button
                  className="modal-close"
                  type="button"
                  aria-label="Close more info"
                  onClick={() => setMoreInfoOpen(false)}
                >
                  x
                </button>
              </div>

              <div className="more-info-grid">
                <Panel headingLevel={3} title="Priority bands" desc="Clinical score band distribution">
                  <VBarChart
                    title={`${DOMAIN_LABELS[domain]} covered people by priority band`}
                    data={bands.map((row) => ({ name: row.label, v: row.memberCount }))}
                    yLabel="Covered people"
                    format={formatCount}
                  />
                </Panel>

                <Panel
                  headingLevel={3}
                  title="Trend across evidence periods"
                  desc="Opportunity volume by evidence period"
                >
                  {trends && (
                    <table className="ae-table">
                      <thead>
                        <tr>
                          <th>Series</th>
                          <th>Trend</th>
                          <th style={{ textAlign: "right" }}>Latest covered people</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(trends.series).map(([name, values]) => (
                          <tr key={name}>
                            <td>{name.replace(/([A-Z])/g, " $1").toLowerCase()}</td>
                            <td>
                              <Sparkline
                                values={values}
                                width={120}
                                height={26}
                                label={`${name.replace(/([A-Z])/g, " $1").toLowerCase()} trend`}
                                format={formatCount}
                              />
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 700 }}>
                              {formatCount(values[values.length - 1])}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Panel>

                <Panel
                  headingLevel={3}
                  title="Condition subdomains"
                  desc="Cells under the minimum cohort size are suppressed"
                  full
                >
                  <HBarChart
                    title={`${DOMAIN_LABELS[domain]} covered people by condition subdomain`}
                    data={subdomains.map((row) => ({ name: row.label, v: row.memberCount }))}
                    format={formatCount}
                  />
                </Panel>

                <Panel
                  headingLevel={3}
                  title="Why covered people were excluded"
                  desc="Guardrail / suppression reasons (aggregate)"
                  full
                >
                  <table className="ae-table">
                    <thead>
                      <tr>
                        <th>Reason</th>
                        <th style={{ textAlign: "right" }}>Covered people</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suppressions.length === 0 && (
                        <tr>
                          <td colSpan={2} style={{ color: "var(--bi-muted)" }}>
                            No suppressed covered people in this evidence period
                          </td>
                        </tr>
                      )}
                      {suppressions.map((row) => (
                        <tr key={row.key}>
                          <td>{row.label}</td>
                          <td style={{ textAlign: "right", fontWeight: 700 }}>
                            {row.suppressed ? (
                              <span className="suppressed-chip">&lt; {enums.minCohortSize}</span>
                            ) : (
                              formatCount(row.memberCount)
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Panel>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <Shell
      tenant={tenant}
      auth={auth}
      activeNav="opportunities"
      title={`Opportunity Explorer — ${DOMAIN_LABELS[domain]}`}
      subtitle={`${activeProgramName} · evidence period ${activeRun?.runMonth || "Unavailable"} · aggregate view — no person-level data`}
    >
      {body()}
    </Shell>
  );
}
